import type { ChangeEvent } from 'react';
import { FormField, Select } from '../../components/ui/FormField.js';
import type { RoleOption } from '../../hooks/useRoleOptions.js';

/**
 * 376 (#1262, design `Roles.html`): an account holds Customer plus AT MOST ONE staff role, and SuperAdmin never
 * combines. The platform decides (`backend/src/shared/roleSet.ts`); these are the panel's words and controls for it.
 */
export const CUSTOMER = 'Customer';
export const SUPERADMIN = 'SuperAdmin';

/** The held roles split into the staff roles (all but Customer) and whether Customer is held. */
export function splitHeld(held: string[]): { staff: string[]; customer: boolean } {
  return {
    staff: [...new Set(held.filter((n) => n !== CUSTOMER))].sort(),
    customer: held.includes(CUSTOMER),
  };
}

/** Whether these roles break the rule (an account from before 376's clean-up). */
export function breaksRule(held: string[]): boolean {
  const set = new Set(held);
  return (set.has(SUPERADMIN) && set.size > 1) || splitHeld(held).staff.length > 1;
}

const REASONS: Record<NonNullable<RoleOption['reason']> | 'needs_supplier', string> = {
  superadmin_role_only: 'only a SuperAdmin gives it',
  staff_role_admin_only: 'only an administrator gives staff roles',
  role_not_grantable: "needs powers you don't hold",
  needs_supplier: "give it from the person's page (needs a supplier)",
};

/**
 * 376 (design B): the one staff role. "None (customer only)" first, then Platform, Organisation (the supplier is chosen
 * next) and All powers (SuperAdmin). A role this viewer may not give is greyed with the reason; in the bulk bar an
 * organisation role cannot be given (no supplier there).
 */
export function StaffRoleSelect({
  id,
  value,
  onChange,
  options,
  bulk = false,
  'aria-label': ariaLabel,
}: {
  id: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: RoleOption[];
  bulk?: boolean;
  'aria-label'?: string;
}) {
  const reasonOf = (o: RoleOption) =>
    bulk && o.kind === 'organisation' ? 'needs_supplier' : o.grantable ? null : o.reason;
  const staff = options
    .filter((o) => o.name !== CUSTOMER)
    .sort((a, b) => a.name.localeCompare(b.name));
  const groups: Array<[string, RoleOption[]]> = [
    ['Platform', staff.filter((o) => o.kind === 'platform' && o.name !== SUPERADMIN)],
    ['Organisation — choose the supplier next', staff.filter((o) => o.kind === 'organisation')],
    ['All powers', staff.filter((o) => o.name === SUPERADMIN)],
  ];
  // A held role this list does not name (an older backend's fallback list) still shows as itself, never as "None".
  const unlisted = value !== '' && !staff.some((o) => o.name === value);
  return (
    <Select id={id} name={id} value={value} onChange={onChange} aria-label={ariaLabel}>
      <option value="">None (customer only)</option>
      {unlisted && (
        <option value={value} disabled>
          {value}
        </option>
      )}
      {groups
        .filter(([, list]) => list.length > 0)
        .map(([label, list]) => (
          <optgroup key={label} label={label}>
            {list.map((o) => {
              const reason = reasonOf(o);
              return (
                <option key={o.name} value={o.name} disabled={reason !== null}>
                  {reason ? `${o.name} — ${REASONS[reason]}` : o.name}
                </option>
              );
            })}
          </optgroup>
        ))}
    </Select>
  );
}

function Chip({ name }: { name: string }) {
  const tone =
    name === CUSTOMER
      ? 'bg-semantic-success-bg text-semantic-success-text'
      : name === SUPERADMIN
        ? 'bg-accent-purple-soft text-accent-purple'
        : 'bg-brand-blue-soft text-primary-deep';
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${tone}`}>{name}</span>;
}

/** The roles as chips: "Customer + CRM". */
export function RoleChips({ names, testId }: { names: string[]; testId?: string }) {
  return (
    <span data-testid={testId} className="inline-flex flex-wrap items-center gap-1.5">
      {names.map((n, i) => (
        <span key={n} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-steel">+</span>}
          <Chip name={n} />
        </span>
      ))}
    </span>
  );
}

/** The roles an account will hold after saving these two controls. */
export function resultingRoles(staffRole: string, customer: boolean): string[] {
  if (staffRole === SUPERADMIN) return [SUPERADMIN];
  return [...(customer ? [CUSTOMER] : []), ...(staffRole ? [staffRole] : [])];
}

/**
 * 376 (design A, C): the Role card's two controls and what saving means. Choosing SuperAdmin turns Customer off (a
 * SuperAdmin holds no other role).
 */
export function RoleControls({
  idPrefix,
  staffRole,
  customer,
  onStaffRole,
  onCustomer,
  options,
  dropsCustomer,
}: {
  idPrefix: string;
  staffRole: string;
  customer: boolean;
  onStaffRole: (name: string) => void;
  onCustomer: (on: boolean) => void;
  options: RoleOption[];
  /** True when the account holds Customer now and SuperAdmin would remove it. */
  dropsCustomer?: boolean;
}) {
  const superAdmin = staffRole === SUPERADMIN;
  const result = resultingRoles(staffRole, customer);
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          label="Staff role"
          name={`${idPrefix}_staff_role`}
          hint="At most one. “None” makes this a customer-only account."
        >
          <StaffRoleSelect
            id={`${idPrefix}_staff_role`}
            value={staffRole}
            options={options}
            onChange={(e) => onStaffRole(e.currentTarget.value)}
          />
        </FormField>
        <label
          className={`flex items-start gap-2.5 rounded-lg border border-hairline px-3 py-2.5 text-sm sm:mt-6 ${superAdmin ? 'opacity-60' : ''}`}
        >
          <input
            type="checkbox"
            className="mt-0.5"
            checked={superAdmin ? false : customer}
            disabled={superAdmin}
            onChange={(e) => onCustomer(e.currentTarget.checked)}
          />
          <span>
            <span className="font-semibold text-ink">Also a customer</span>
            <span className="block text-xs text-steel">
              {superAdmin
                ? 'A SuperAdmin holds no other role.'
                : 'They can book cars at their campus as themselves. Staff powers always win where both apply.'}
            </span>
          </span>
        </label>
      </div>
      <p
        data-testid="role-result"
        className="flex flex-wrap items-center gap-2 rounded-md bg-surface-feature px-3 py-2 text-sm"
      >
        <span className="text-steel">After saving, this account is:</span>
        {result.length > 0 ? (
          <RoleChips names={result} />
        ) : (
          <span className="text-steel">no role</span>
        )}
        {superAdmin && dropsCustomer && (
          <span className="text-xs text-steel">(Customer will be removed)</span>
        )}
      </p>
    </div>
  );
}
